import { Color, Spacing } from '@persona-id/design-tokens';
import { Control, Controller } from 'react-hook-form';
import styled from 'styled-components';

import { AddFunctionFormData } from 'dashboard/forms/FormGraphNodeConfiguration/NodeFunctionsConfigurationTab/AddFunctionModal';
import { PRESET_TYPE_TO_LABEL } from 'dashboard/forms/FormGraphNodeConfiguration/NodeFunctionsConfigurationTab/NodeFunctionsConfigurationTab';
import { PresetType, ServerPresetSummary } from 'dashboard/models/graph-backend';
import Card from 'design-system/components/Card';

import { NewGraphNodeFormData } from './FormCreateGraphNode';
import PresetSchemaSummary from './PresetSchemaSummary';

interface NodeFunctionSelectorProps {
  control: Control<NewGraphNodeFormData, object> | Control<AddFunctionFormData, object>;
  presetSummaries: ServerPresetSummary[];
}

const Container = styled.div`
  display: flex;
  max-height: 500px;
  /* border-top: 1px solid ${Color.CoreSlate100}; */
`;

const PresetContainer = styled.div`
  flex: 2;
  padding: ${Spacing[5]};
  border-right: 1px solid ${Color.CoreSlate100};
  overflow-y: auto;
`;

const SummaryContainer = styled.div`
  flex: 3;
  background-color: ${Color.BackgroundSecondary};
  padding: ${Spacing[5]};
`;

const PresetCard = styled.div<{ selected: boolean }>`
  padding: ${Spacing[3]};
  cursor: pointer;
  border-radius: ${Spacing[1]};
  border: 1px solid ${Color.BorderHairline};
  margin-bottom: ${Spacing[2]};
  ${({ selected }) => selected && `border: 1px solid ${Color.CorePurple700};`}
`;

const Red = styled.span`
  background-color: ${Color.CoreRed100};
`;

const Green = styled.span`
  background-color: ${Color.CoreGreen200};
`;

export interface PresetExample {
  rawInput: JSX.Element;
  output: JSX.Element;
}

export interface PresetInfo {
  preset: PresetType;
  examples: PresetExample[];
}

const presetInfoData: PresetInfo[] = [
  {
    preset: PresetType.NormalizeAddress,
    examples: [
      {
        rawInput: (
          <span>
            1600 Pennsylvania <Red>Avenue</Red> NW, Washington DC 20500
          </span>
        ),
        output: (
          <span>
            1600 PENNSYLVANIA <Green>AVE</Green> NW WASHINGTON DC 20500
          </span>
        ),
      },
      {
        rawInput: (
          <span>
            1 Ferry <Red>Building</Red>, San Francisco, CA 94105
          </span>
        ),
        output: (
          <span>
            1 FERRY <Green>BLDG</Green>, SAN FRANCISCO, CA 94105
          </span>
        ),
      },
    ],
  },
  {
    preset: PresetType.NormalizeDate,
    examples: [
      {
        rawInput: (
          <span>
            05<Red>/</Red>25<Red>/</Red>2024
          </span>
        ),
        output: (
          <span>
            05<Green>-</Green>25<Green>-</Green>2024
          </span>
        ),
      },
      {
        rawInput: (
          <span>
            31 <Red>December</Red> 2024
          </span>
        ),
        output: (
          <span>
            <Green>12-</Green>31<Green>-</Green>2024
          </span>
        ),
      },
      {
        rawInput: <span>2025-13-40</span>,
        output: <span>Invalid</span>,
      },
      {
        rawInput: <span>abc</span>,
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.NormalizeDateTime,
    examples: [
      {
        rawInput: (
          <span>
            05<Red>/</Red>25<Red>/</Red>2024
          </span>
        ),
        output: (
          <span>
            2024<Green>-</Green>05<Green>-</Green>25
          </span>
        ),
      },
      {
        rawInput: (
          <span>
            31 <Red>December</Red> 2024
          </span>
        ),
        output: (
          <span>
            2024<Green>-12-</Green>31
          </span>
        ),
      },
      {
        rawInput: <span>2025-13-40</span>,
        output: <span>Invalid</span>,
      },
      {
        rawInput: <span>abc</span>,
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.NormalizeDecimal,
    examples: [
      {
        rawInput: (
          <span>
            1<Red>.0</Red>
          </span>
        ),
        output: (
          <span>
            <Green>1</Green>
          </span>
        ),
      },
      {
        rawInput: (
          <span>
            123.4<Red>56789</Red>
          </span>
        ),
        output: (
          <span>
            123.4<Green>6</Green>
          </span>
        ),
      },
      {
        rawInput: (
          <span>
            <Red>00</Red>123.40<Red>00</Red>
          </span>
        ),
        output: <span>123.40</span>,
      },
      {
        rawInput: (
          <span>
            -45.6<Red>789</Red>
          </span>
        ),
        output: (
          <span>
            -45.6<Green>8</Green>
          </span>
        ),
      },
      {
        rawInput: (
          <span>
            -<Red>0</Red>45.6
          </span>
        ),
        output: (
          <span>
            -<Green>45.6</Green>
          </span>
        ),
      },
      {
        rawInput: (
          <span>
            42<Red>,</Red>00
          </span>
        ),
        output: (
          <span>
            42<Green>.</Green>00
          </span>
        ),
      },
      {
        rawInput: <span>twelve</span>,
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.NormalizeEmail,
    examples: [
      {
        rawInput: (
          <span>
            example<Red>.</Red>email<Red>+alias</Red>@gmail.com
          </span>
        ),
        output: <span>exampleemail@gmail.com</span>,
      },
      {
        rawInput: (
          <span>
            example@gmail.<Red>COM</Red>
          </span>
        ),
        output: (
          <span>
            example@gmail.<Green>com</Green>
          </span>
        ),
      },
      {
        rawInput: (
          <span>
            example<Red>+user</Red>@gmail.com
          </span>
        ),
        output: <span>example@gmail.com</span>,
      },
    ],
  },
  {
    preset: PresetType.NormalizeGovernmentId,
    examples: [
      {
        rawInput: (
          <div>
            <span>
              identification_number: AB<Red>-</Red>123456
            </span>
            <br></br>
            <span>identification_class: pp</span>
          </div>
        ),
        output: (
          <div>
            <span>
              identification_number: <Green>AB123456</Green>
            </span>
            <br></br>
            <span>identification_class: pp</span>
          </div>
        ),
      },
      {
        rawInput: (
          <div>
            <span>
              identification_number: AB<Red>-##</Red>123456
            </span>
            <br></br>
            <span>identification_class: pp</span>
          </div>
        ),
        output: (
          <div>
            <span>
              identification_number: <Green>AB123456</Green>
            </span>
            <br></br>
            <span>identification_class: pp</span>
          </div>
        ),
      },
      {
        rawInput: (
          <div>
            <span>identification_number: AB-12</span>
            <br></br>
            <span>identification_class: pp</span>
          </div>
        ),
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.NormalizeName,
    examples: [
      {
        rawInput: (
          <div>
            <span>
              First: <Red>J</Red>ohn
            </span>
            <br></br>
            <span>Middle:</span>
            <br></br>
            <span>
              Last: <Red>D</Red>oe
            </span>
            <br></br>
          </div>
        ),
        output: (
          <span>
            <Green>j</Green>ohn <Green>d</Green>oe
          </span>
        ),
      },
      {
        rawInput: (
          <div>
            <span>
              First: <Red>J</Red>OHN
            </span>
            <br></br>
            <span>Middle: walter</span>
            <br></br>
            <span>
              Last: <Red>D</Red>oe
            </span>
            <br></br>
          </div>
        ),
        output: (
          <span>
            <Green>john</Green> walter <Green>d</Green>oe
          </span>
        ),
      },
      {
        rawInput: (
          <div>
            <span>First:</span>
            <br></br>
            <span>Middle: John</span>
            <br></br>
            <span>Last: Doe</span>
            <br></br>
          </div>
        ),
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.NormalizeIdDocumentNumber,
    examples: [
      {
        rawInput: (
          <div>
            <span>document_number: A123/5</span>
            <br></br>
            <span>document_type: pp</span>
            <br></br>
            <span>
              issuing_country: U<Red>s</Red>
            </span>
            <br></br>
            <span>
              issuing_subdivision: <Red>tx</Red>
            </span>
            <br></br>
          </div>
        ),
        output: (
          <div>
            <span>document_number: A123/5</span>
            <br></br>
            <span>document_type: pp</span>
            <br></br>
            <span>
              issuing_country: U<Green>S</Green>
            </span>
            <br></br>
            <span>
              issuing_subdivision: <Green>TX</Green>
            </span>
            <br></br>
          </div>
        ),
      },
      {
        rawInput: (
          <div>
            <span>document_number: unavl</span>
            <br></br>
            <span>document_type: Passport</span>
            <br></br>
            <span>issuing_country: US</span>
            <br></br>
            <span>issuing_subdivision: TX</span>
            <br></br>
          </div>
        ),
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.NormalizeIpAddress,
    examples: [
      {
        rawInput: <span>192.169.2.1</span>,
        output: <span>192.169.2.1</span>,
      },
      {
        rawInput: <span>192.168.0.1/24</span>,
        output: <span>Invalid - CIDR Mask</span>,
      },
      {
        rawInput: <span>127.0.0.1</span>,
        output: <span>Invalid - Private or Loopback</span>,
      },
    ],
  },
  {
    preset: PresetType.NormalizePhoneNumber,
    examples: [
      {
        rawInput: (
          <span>
            +1 <Red>(</Red>202<Red>) </Red>555-0125
          </span>
        ),
        output: (
          <span>
            +1 202<Green>-</Green>555-0125
          </span>
        ),
      },
      {
        rawInput: <span>55 11 99999-8888</span>,
        output: (
          <span>
            <Green>+</Green>55 11 99999-8888
          </span>
        ),
      },
      {
        rawInput: <span>2025550125</span>,
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.NormalizePostalCode,
    examples: [
      {
        rawInput: <span>94140</span>,
        output: <span>94140</span>,
      },
      {
        rawInput: (
          <span>
            SW1A<Red> </Red>1AA
          </span>
        ),
        output: (
          <span>
            <Green>SWIA1AA</Green>
          </span>
        ),
      },
      {
        rawInput: <span>@123</span>,
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.NormalizeSocialSecurityNumber,
    examples: [
      {
        rawInput: (
          <span>
            223<Red> </Red>45<Red> </Red>6789
          </span>
        ),
        output: (
          <span>
            <Green>223456789</Green>
          </span>
        ),
      },
      {
        rawInput: (
          <span>
            223<Red>-</Red>456<Red>-</Red>789
          </span>
        ),
        output: (
          <span>
            <Green>223456789</Green>
          </span>
        ),
      },
      {
        rawInput: <span>111111111</span>,
        output: <span>Invalid</span>,
      },
      {
        rawInput: <span>123456789</span>,
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.ValidateAccount,
    examples: [
      {
        rawInput: <span>act_79ivYeW7VrVM4yeFi1SdB9JRsgZY</span>,
        output: <span>Valid</span>,
      },
      {
        rawInput: <span>txn_3rjBrBcZdeMyCv4geJMcGBUvRfWV</span>,
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.ValidateBoolean,
    examples: [
      {
        rawInput: <span>false</span>,
        output: <span>Valid</span>,
      },
      {
        rawInput: <span>true</span>,
        output: <span>Valid</span>,
      },
      {
        rawInput: <span>asdjlasjk</span>,
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.ValidateInteger,
    examples: [
      {
        rawInput: <span>123</span>,
        output: <span>Valid</span>,
      },
      {
        rawInput: <span>-123</span>,
        output: <span>Valid</span>,
      },
      {
        rawInput: <span>0</span>,
        output: <span>Valid</span>,
      },
      {
        rawInput: <span>12.34</span>,
        output: <span>Invalid</span>,
      },
      {
        rawInput: <span>123abc</span>,
        output: <span>Invalid</span>,
      },
      {
        rawInput: <span>12,345</span>,
        output: <span>Invalid</span>,
      },
      {
        rawInput: <span>True</span>,
        output: <span>Invalid</span>,
      },
      {
        rawInput: <span>None</span>,
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.ValidateSocialSecurityNumberLast4,
    examples: [
      {
        rawInput: <span>6789</span>,
        output: <span>Valid</span>,
      },
      {
        rawInput: <span>67X9</span>,
        output: <span>Invalid</span>,
      },
      {
        rawInput: <span>678</span>,
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.ValidateString,
    examples: [
      {
        rawInput: <span>Hello</span>,
        output: <span>Valid</span>,
      },
      {
        rawInput: <span>asdjahkd</span>,
        output: <span>Valid</span>,
      },
      {
        rawInput: <span>1234</span>,
        output: <span>Valid</span>,
      },
    ],
  },
  {
    preset: PresetType.ValidateUuid,
    examples: [
      {
        rawInput: <span>123e4567-e89b-12d3-a456-426614174000</span>,
        output: <span>Valid</span>,
      },
      {
        rawInput: <span>abcd1234-ab12-ab12-ab12-abcd1234abcd</span>,
        output: <span>Valid</span>,
      },
      {
        rawInput: <span>12345678-1234-5678-1234-567812345678</span>,
        output: <span>Valid</span>,
      },
      {
        rawInput: <span>example-string</span>,
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.ValidateBooleanFalse,
    examples: [
      {
        rawInput: <span>false</span>,
        output: <span>Valid</span>,
      },
      {
        rawInput: <span>true</span>,
        output: <span>Invalid</span>,
      },
    ],
  },
  {
    preset: PresetType.RunRegex,
    examples: [
      {
        rawInput: (
          <div>
            <span>
              value: <Red>abc</Red>123<Red>def</Red>
            </span>
            <br></br>
            <span>regex: \\d+</span>
          </div>
        ),
        output: (
          <div>
            <span>match_value: 123</span>
            <br></br>
            <span>match_found: true</span>
          </div>
        ),
      },
      {
        rawInput: (
          <div>
            <span>value: abc123</span>
            <br></br>
            <span>regex: [A-Z]+</span>
          </div>
        ),
        output: (
          <div>
            <span>match_value:</span>
            <br></br>
            <span>match_found: false</span>
          </div>
        ),
      },
    ],
  },
];

const NodeFunctionSelector = ({ presetSummaries, control }: NodeFunctionSelectorProps) => {
  return (
    <Controller
      control={control as Control<AddFunctionFormData>}
      name='presetType'
      defaultValue={presetSummaries?.[0]?.preset}
      render={({ field: { onChange, value } }) => {
        const presetSummary = presetSummaries.find((ps) => ps.preset === value);
        return (
          <Container>
            <PresetContainer>
              {presetSummaries
                .filter(
                  (presetSummary) => presetSummary.preset !== PresetType.NormalizeSimilarityTargets,
                )
                .map((preset) => {
                  return (
                    <PresetCard
                      id={`node-function-selector__summary-card-${preset.preset}`}
                      onClick={() => onChange(preset.preset)}
                      selected={preset.preset === value}
                      key={preset.preset}
                    >
                      {PRESET_TYPE_TO_LABEL[preset.preset]}
                    </PresetCard>
                  );
                })}
            </PresetContainer>
            <SummaryContainer>
              <Card>
                <Card.Body>
                  <Card.Title>{PRESET_TYPE_TO_LABEL[value]}</Card.Title>
                  {presetSummary && (
                    <PresetSchemaSummary
                      presetInfo={presetInfoData.find((x) => x.preset === value)}
                    />
                  )}
                </Card.Body>
              </Card>
            </SummaryContainer>
          </Container>
        );
      }}
    />
  );
};

export default NodeFunctionSelector;
